import { useRef, useCallback, useEffect } from "react";

interface UseDebouncedObjectParams<T> {
  initialValue: T;
  onDebounce: (value: T) => void;
  delay?: number;
}

export const useDebounced = <T>({
  initialValue,
  onDebounce,
  delay = 400,
}: UseDebouncedObjectParams<T>) => {
  const bufferRef = useRef<T>(initialValue);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(onDebounce);

  // Mantener callback actualizado sin romper dependencias
  useEffect(() => {
    callbackRef.current = onDebounce;
  }, [onDebounce]);

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callbackRef.current(bufferRef.current);
    }, delay);
  }, [delay]);

  const update = useCallback(
    (updater: (prev: T) => T) => {
      bufferRef.current = updater(bufferRef.current);
      flush();
    },
    [flush]
  );

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return {
    update,
    cancel,
  };
};
